<template>
  <div style="display: inline-flex; align-items: center; gap: 4px;">
    <b-btn
      v-b-tooltip.hover
      title="Visualizar"
      variant="none"
      class="btn btn-none rounded-pill m-0 icone-acao p-0 btn-none"
      :disabled="!gridProps.row.hasFile"
      @click="abrirArquivo(gridProps.row)"
    >
      <font-awesome-icon
        :icon="faExternalLinkAlt"
        class="text-primary"
      />
    </b-btn>

    <b-btn
      v-b-tooltip.hover
      title="Histórico"
      variant="none"
      class="btn-none icone-acao m-0 p-0"
      @click="openModalHistorico()"
    >
      <font-awesome-icon
        icon="fa-sharp fa-solid fa-list"
        class="text-primary"
      />
    </b-btn>

    <b-btn
      v-b-tooltip.hover
      title="Observação"
      variant="none"
      class="btn-none rounded-pill m-0 icone-acao p-0"
      @click="openModalObservacao()"
    >
      <font-awesome-icon
        icon="fa-regular fa-message"
        :class="{
          'text-success': todasObservacoesLidas,
          'text-warning': !todasObservacoesLidas && temObservacaoNova,
          'text-primary': !todasObservacoesLidas && !temObservacaoNova
        }"
      />
    </b-btn>

    <span>
      <modal-whatsapp
        :disabled="!gridProps.row.hasFile"
        tipo-rotina="CCMEI"
        :row="gridProps.row"
        :api="api"
      />
    </span>

    <div
      v-b-tooltip.hover
      title="Enviar por e-mail"
    >
      <button
        variant="none"
        class="btn btn-none rounded-pill m-0 icone-acao p-0 btn-none"
        :disabled="!gridProps.row.hasFile"
        @click="abrirEnvioEmail(gridProps.row)"
      >
        <feather-icon
          icon="SendIcon"
          :class="gridProps.row.emailEnviado || emailEnviado ? 'text-success' : 'text-primary'"
          size="20"
        />
      </button>
    </div>

    <modal-observacao
      ref="modalObservacao"
      v-model="gridProps.row"
      tipo="CcMei"
      :api="api"
      :notificar="{ sucesso: notficarSucesso, erro: notificarErro }"
      @hidden="fecharObservacao($event)"
    />

    <visualizar-arquivo
      v-if="mostrarArquivo && arquivo.url"
      :url="arquivo.url"
      @hidden="fecharArquivo()"
    />

    <envio-email
      ref="modalEnvioEmail"
      :title="`Enviar Certificado CCMEI ${gridProps.row.razaoSocial}`"
      :sugestao="[(gridProps.row.emailEmpresa) || '']"
      :sugestao-email-adicional="gridProps.row.emailAdicional ? gridProps.row.emailAdicional.split(',') : undefined"
      @enviarEmail="enviarCCMei"
      @hide="docEmail = null"
    />

    <modal-historico
      :ref="`modalHistorico${gridProps.row.id}`"
      rotina="cc-mei"
      des-de="2026"
      :empresa="gridProps.row"
      tipo="CcMei"
      :api="api"
      :coluna-historico="colunaHistorico"
      :filtros-rapidos="[]"
      :filtro-by-historico="['ConsultadoEm']"
      :acoes-lote="{}"
      :acaos-individuais="({
        download: false,
        enviarEmail: true,
        enviarWhatsApp: true,
        observacao: true,
      })"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import envioHistoricoApi from '@app/shared/services/envio-historico-api'
import api from '../../../services/api'
import colunasHistorico from '../historico'

export default {
  components: {
    FontAwesomeIcon,
    VisualizarArquivo: () => import('@pilar/components/visualizar-arquivo/VisualizarArquivo.vue'),
    ModalObservacao: () => import('@/app/shared/components/modal-observacao/ModalObservacao.vue'),
    ModalHistorico: () => import('@/app/shared/components/historico/ModalHistorico.vue'),
    ModalWhatsapp: () => import('@/app/shared/components/whatsapp/ModalWhatsapp.vue'),
    EnvioEmail: () => import('@/app/shared/components/envio-email/EnvioEmail.vue'),
  },
  props: {
    gridProps: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      api,
      faExternalLinkAlt,
      mostrarArquivo: false,
      arquivo: {},
      docEmail: null,
      emailEnviado: false,
    }
  },
  computed: {
    temObservacaoNova() {
      return this.gridProps?.row?.temObservacao ?? false
    },
    todasObservacoesLidas() {
      return this.gridProps?.row?.todasObservacoesLidas ?? false
    },
    colunaHistorico() {
      return colunasHistorico
    },
  },
  methods: {
    abrirArquivo(linha) {
      api.getURL(linha.id)
        .then(payload => {
          this.arquivo.url = payload.data
          this.mostrarArquivo = true
        })
        .catch(err => {
          this.fecharArquivo()
          this.notificarErro('Não foi possível abrir a visualização do arquivo.')
          throw err
        })
    },

    fecharArquivo() {
      this.mostrarArquivo = false
      this.arquivo = {}
    },

    openModalHistorico() {
      this.$refs[`modalHistorico${this.gridProps.row.id}`].abrirModal()
    },

    openModalObservacao() {
      this.$refs.modalObservacao.show()
    },

    fecharObservacao(observacaoNova) {
      this.gridProps.row.temObservacao = observacaoNova.hasObservacaoNova
      this.gridProps.row.todasObservacoesLidas = observacaoNova.observacaoLida
    },

    abrirEnvioEmail(documento) {
      this.docEmail = documento
      this.$refs.modalEnvioEmail.$refs.modalEnviarEmail.show()
    },

    enviarCCMei(itens) {
      if (!itens.emailsPara || itens.emailsPara.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Informe ao menos um e-mail destinatário (Para)',
            icon: 'fa-solid fa-circle-exclamation',
            variant: 'danger',
          },
        })
      } else {
        const { id: idRegistro, empresaId, razaoSocial } = this.gridProps.row

        api.notificaCcMei(idRegistro, {
          nomeEmpresa: razaoSocial,
          emailEmpresa: itens.emailsPara.join(','),
          emailCc: itens.emailsCc ? itens.emailsCc.join(',') : '',
          mensagemAdicional: itens.mensagensAdicional,
          definirEmailPadrao: itens.definirEmailPadroes,
        }).then(res => {
          if (res && res.data === false) {
            this.$erro('Falha ao enviar e-mail.')
            return
          }
          const payload = {
            tipoRotina: 30, // ETipoRotina.CCMei
            tipoCanal: 0, // ETipoCanal.Email
            tipoRegistro: 35, // ETipoRegistro.CCMei
            idRegistro,
            empresaId,
            destinatario: `${itens.emailsPara.join(';')}${itens.emailsCc && itens.emailsCc.length ? `;${itens.emailsCc.join(';')}` : ''}`,
          }
          envioHistoricoApi.registrarManual(payload).catch(err => this.$erro(err))
          this.emailEnviado = true
          this.$set(this.gridProps.row, 'emailEnviado', true)
          this.$sucesso('E-mail enviado com sucesso!')
        }).catch(err => {
          this.$erro((err && err.response && err.response.data && (err.response.data.message || err.response.data.errors?.[0])) || err.message || 'Erro ao enviar e-mail.')
        })
      }
    },

    notficarSucesso(msg) {
      this.$sucesso(msg)
    },

    notificarErro(msg) {
      this.$erro(msg)
    },
  },
}
</script>
